// ─────────────────────────────────────────────────────────────────────────────
// endpoints.js — the ONE authored source for what the dashboard talks to.
//
// Owner ruling 2026-08-01: the portrait phone surface is the Claude Design
// document itself (public/phone/index.html), carrying its own dc-runtime. It is
// served verbatim out of public/ and is NEVER compiled by CRA, so it cannot
// import the React-hook core in src/ — and a `class Component extends DCLogic`
// could not call hooks even if it could reach them.
//
// That left the nine endpoints, the Bearer idiom and the poll cadence needing to
// exist in two places, which is exactly the fork "ONE CORE, EVERY WINDOW" exists
// to prevent. So the FACTS THAT DRIFT are extracted here, once:
//
//   • this file is imported by authHeaders.ts and the seven hooks   (desktop)
//   • this file is copied verbatim to public/phone/ at prestart/prebuild,
//     and loaded by the design page's Component body                (phone)
//
// One authored source, two consumers. The copy in public/phone/ is a BUILD
// ARTIFACT — never edit it, never commit it; edit this file.
//
// PLAIN JS ON PURPOSE, and it must stay that way:
//   • no TypeScript — the browser loads it directly, unbuilt;
//   • no imports — it is the root of the graph on both sides;
//   • native ES module syntax only, so `import()` works from a static page.
// Anything richer than this belongs in the hooks (desktop) or the Component
// body (phone), not here.
// ─────────────────────────────────────────────────────────────────────────────

/** Every feed polls on the same cadence. Changing it here changes it everywhere. */
export const POLL_MS = 30000;

/**
 * The ONE auth idiom for every dashboard feed.
 * The token is injected on `window.__rexyToken` by the shell (unchanged contract).
 * @param {boolean} [json] also send a JSON content-type (for the two POSTs)
 * @returns {Record<string, string>}
 */
export function authHeaders(json = false) {
  const token = /** @type {any} */ (window).__rexyToken;
  /** @type {Record<string, string>} */
  const h = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

const API = '/api/rexy';

// ── the nine endpoints ───────────────────────────────────────────────────────
// Constants for the fixed paths; functions where the caller supplies a scope.
// The query-string shape is part of the contract — the backend interprets
// `date` in the USER-SET timezone, never UTC (see useCalendar's dayKey).

/** GET — the activity index. */
export const ACTIVITY = `${API}/activity`;

/** GET — one activity row's drill-in detail, 64-hex-scoped. */
export const activityDetail = (ref) =>
  `${API}/activity/detail?ref=${encodeURIComponent(ref)}`;

// ── ALPHA'S ACTIVITY — THE SIBLING, NOT A PARAM (owner ruling 2026-09-01) ──
// GROWTH ▸ CEO's verification surface. A `?domain=` on hers would have been a
// filter; three things differ and none of them is one — the SCOPE (GROWTH minus
// Alpha's own `ceo` root), the CHIP JOIN (her lane records, not Rexy's — Rexy's
// would render every GROWTH row pending forever) and the CLOCK (HH:10 off
// Alpha's ledger). Same response shape, so ONE renderer serves both.
const GROWTH_API = '/api/growth';

/** GET — Alpha's GROWTH-scoped activity index. */
export const GROWTH_ACTIVITY = `${GROWTH_API}/activity`;

/** GET — one GROWTH activity row's drill-in, out of Alpha's lane. */
export const growthActivityDetail = (ref) =>
  `${GROWTH_API}/activity/detail?ref=${encodeURIComponent(ref)}`;

/** GET — one month's per-day counts + per-store source status. `month` is 1-based. */
export const calendar = (year, month) => `${API}/calendar?year=${year}&month=${month}`;

/** GET — one day's drill-down. `dateKey` is local "YYYY-MM-DD", never toISOString. */
export const calendarDay = (dateKey) =>
  `${API}/calendar/day?date=${encodeURIComponent(dateKey)}`;

/** GET the stored timezone + server-local now; POST to write it. */
export const SETTINGS = `${API}/settings`;

/** GET — her nightly Tier-1 synthesis, nights newest-first. */
export const DREAMSTATE = `${API}/dreamstate-index`;

/** GET — per-event notes grouped under the pulse hour. */
export const NOTES = `${API}/notes`;

/** GET — the report windows, newest-first. */
export const REPORT_INDEX = `${API}/report-index`;

/** POST — answer a waiting report: { report_id, answer: 'accept' | 'reject' }. */
export const REPORT_ANSWER = `${API}/report-answer`;

/** GET — the two next fires plus the latest <=12 accrued slots. */
export const PULSE_SCHEDULE = `${API}/pulse-schedule`;

// ── the declaration-driven domain surface (GROWTH shell, v4 contract §6.3) ───
// A domain that declares a root vocabulary renders its own surface. BOTH of these
// are GET-ONLY here on purpose: the overview renders, and a render is not a
// mutation. The PUT twin of the board exists server-side and is the SOLE writer,
// but no read surface may reach for it — so it is deliberately not exported.

/** GET — the structural snapshot: domains, agents, surfaces (+ declared `tabs`). */
export const OS_STRUCTURE = '/api/os/structure';

/** GET — one domain's board. An absent store answers {"board": {}} and that is CORRECT. */
export const domainBoard = (domain) =>
  `/api/domain-board/${encodeURIComponent(domain)}`;

// ── the chat lane ────────────────────────────────────────────────────────────
// THERE IS ONLY ONE AUTH IDIOM. An earlier reading of this file claimed the chat
// lane took `X-Rexy-Token` instead of Bearer, because App.tsx's chat call sites
// pass that header explicitly. They do — but it is VESTIGIAL ("Keep X-Rexy-Token
// during transition period", App.tsx:64). The header that actually authenticates
// is `Authorization: Bearer`, attached to EVERY desktop request by the global
// axios interceptor at App.tsx:56, which is easy to miss because no call site
// mentions it.
//
// /api/threads and /api/conversation are both @require_auth, and require_auth
// (server/app.py:2135) reads the Authorization header ONLY — it never looks at
// X-Rexy-Token. Sending the legacy header alone means `no_auth_header`, a
// rejection, and an empty chat. That was the bug.
//
// So this sends Bearer (what authenticates) plus the legacy header (exact parity
// with the interceptor, so the phone surface and the desktop are byte-identical
// on the wire).

/**
 * The chat lane's headers — Bearer is what authenticates; X-Rexy-Token is kept
 * only for parity with the desktop interceptor.
 * @param {boolean} [json] also send a JSON content-type
 * @returns {Record<string, string>}
 */
export function rexyTokenHeaders(json = false) {
  const token = /** @type {any} */ (window).__rexyToken || '';
  /** @type {Record<string, string>} */
  const h = token ? { Authorization: `Bearer ${token}`, 'X-Rexy-Token': token } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/** The homepage thread identity every surface shares. */
export const REXY_HOME_DOMAIN = 'HOMEPAGE';
export const REXY_HOME_AGENT = 'REXY';

/** GET the thread list; POST to open a new one. */
export const THREADS = '/api/threads';
/** POST — close a thread (read-only thereafter). */
export const THREADS_END = '/api/threads/end';

/** GET — one thread's messages. */
export const conversation = (domain, agent, threadId) =>
  `/api/conversation?domain=${encodeURIComponent(domain)}`
  + `&agent=${encodeURIComponent(agent)}`
  + (threadId ? `&thread_id=${encodeURIComponent(threadId)}` : '');

/** POST — send a message. Body carries goal/domain/agent/thread_id/routing_mode/fallback_target. */
export const QUERY = '/api/query';

/** GET — escalation state; routing_mode is DERIVED from it, never set by a surface. */
export const ESCALATION = `${API}/escalation`;

// ── PER-AGENT LANE (frontier lanes, Phase 4) ─────────────────────────────────
// automatic · local · api · sub, stored per agent by the server and read back as
// the server's truth (a fallback saves automatic, so the surface re-reads).

/** GET ?domain=&agent= — the agent's stored lane; POST {domain, agent, lane, provider, model}. */
export const AGENT_LANE = `${API}/agent-lane`;
/** GET — every agent that may carry a lane (roster-derived), with its stored lane. */
export const AGENT_LANE_ELIGIBLE = `${API}/agent-lane/eligible`;
/** GET — the Sub model menu for one provider ('claude' | 'gpt'). */
export const agentLaneModels = (provider) =>
  `${API}/agent-lane/models?provider=${encodeURIComponent(provider)}`;
/** GET — one image a frontier turn carried or made (auth required: never a bare <img src>). */
export const laneImage = (id) => `${API}/lane-image/${encodeURIComponent(id)}`;
